import React, { useCallback, useState } from 'react';
import { useLibraryStore } from '@store/useLibraryStore';
import { useToastStore } from '@store/useToastStore';
import { UploadCloud } from 'lucide-react';
import { cn } from '@lib/utils';
import { validateZipSignature } from '@lib/ingestion';
import { DuplicateBookError } from '~types/errors';
import { ImportProgressUI } from './ImportProgressUI';
import { Loader2 } from 'lucide-react';
import { ReplaceBookDialog } from './ReplaceBookDialog';
import { useGoogleServicesStore } from '@store/useGoogleServicesStore';
import { getGoogleAuthClient } from '@domains/google';
import { useImportController } from '@app/library/useImportController';
import { presentError } from '@app/errors/presentError';
import { Button } from '../ui/Button';
import { useNavigate } from 'react-router-dom';
import { createLogger } from '@lib/logger';

const logger = createLogger('FileUploader');

/**
 * A component for uploading EPUB files, ZIP archives, or directories via drag-and-drop or file selection.
 * Handles user interactions and triggers the book import process through the
 * shared import controller — the store is a read-only projection.
 *
 * @returns A React component rendering the file upload area.
 */
export const FileUploader: React.FC = () => {
  const isImporting = useLibraryStore(state => state.isImporting);
  const controller = useImportController();
  const navigate = useNavigate();

  const { showToast } = useToastStore();
  const [dragActive, setDragActive] = useState(false);
  const [duplicateQueue, setDuplicateQueue] = useState<File[]>([]);
  const currentDuplicate = duplicateQueue[0];

  /**
   * Validates and processes a single file.
   */
  const processSingleFile = useCallback(async (file: File) => {
    const lowerName = file.name.toLowerCase();
    if (lowerName.endsWith('.epub')) {
      const isValid = await validateZipSignature(file);
      if (isValid) {
        try {
          await controller.importFile(file);
        } catch (error) {
          if (error instanceof DuplicateBookError) {
            setDuplicateQueue(prev => [...prev, file]);
          }
        }
      } else {
        showToast(`Invalid EPUB file (header mismatch): ${file.name}`, 'error');
      }
    } else if (lowerName.endsWith('.zip')) {
      const isValid = await validateZipSignature(file);
      if (isValid) {
        await controller.importFiles([file]);
      } else {
        showToast(`Invalid ZIP file (header mismatch): ${file.name}`, 'error');
      }
    } else {
      showToast(`Unsupported file type: ${file.name}`, 'error');
    }
  }, [controller, showToast]);

  /**
   * Processes a list of files (batch import).
   */
  const processFiles = useCallback(async (files: File[]) => {
    if (files.length === 1) {
      await processSingleFile(files[0]);
    } else {
      // Validate files in chunks to balance performance and memory usage
      const validFiles: File[] = [];
      const CONCURRENCY_LIMIT = 10;

      for (let i = 0; i < files.length; i += CONCURRENCY_LIMIT) {
        const chunk = files.slice(i, i + CONCURRENCY_LIMIT);
        const chunkResults = await Promise.all(chunk.map(async (file) => {
          const lowerName = file.name.toLowerCase();
          const isSupported = lowerName.endsWith('.epub') || lowerName.endsWith('.zip');

          if (!isSupported) {
            showToast(`Skipping unsupported file: ${file.name}`, 'error');
            return null;
          }

          const isValid = await validateZipSignature(file);
          if (isValid) {
            return file;
          } else {
            showToast(`Skipping invalid file: ${file.name}`, 'error');
            return null;
          }
        }));

        validFiles.push(...chunkResults.filter((f): f is File => f !== null));
      }

      if (validFiles.length > 0) {
        await controller.importFiles(validFiles);
      }
    }
  }, [controller, processSingleFile, showToast]);

  /**
   * Handles drag events.
   */
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  /**
   * Handles drop events.
   */
  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files);
        await processFiles(files);
      }
    },
    [processFiles]
  );

  /**
   * Handles standard file input change.
   */
  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      await processFiles(files);
    }
    // Reset input
    e.target.value = '';
  };

  const { isServiceConnected } = useGoogleServicesStore();
  const isDriveConnected = isServiceConnected('drive');
  const [isDriveConnecting, setIsDriveConnecting] = useState(false);

  const handleDriveConnect = async () => {
    setIsDriveConnecting(true);
    try {
      await getGoogleAuthClient().connect('drive');
    } catch (error) {
      logger.error("Failed to connect Drive", error);
      showToast("Failed to connect Google Drive", 'error');
    } finally {
      setIsDriveConnecting(false);
    }
  };

  // Browsing Drive navigates to the shelf route (/drive). The token is
  // acquired from this gesture so the shelf can hydrate previews and import
  // without needing a popup of its own.
  const handleBrowseDrive = async () => {
    try {
      await getGoogleAuthClient().getTokenInteractive('drive');
      logger.debug("Drive token valid, opening the Drive shelf");
      navigate('/drive');
    } catch (error) {
      logger.error("Failed to access Drive", error);
      showToast("Failed to access Google Drive. Please reconnect.", 'error');
    }
  };

  return (
    <div className="space-y-4">
      {/* Cloud Integration Header */}
      <div className="flex justify-end">
        {!isDriveConnected ? (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-primary"
            onClick={handleDriveConnect}
            disabled={isDriveConnecting}
            aria-label="Connect Google Drive"
          >
            {isDriveConnecting ? (
              <>
                <Loader2 className="mr-2 h-3 w-3 animate-spin" aria-hidden="true" />
                <span className="sr-only" aria-live="polite">Connecting...</span>
              </>
            ) : null}
            Connect Google Drive
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="gap-2 text-primary border-primary/20 hover:bg-primary/5"
            onClick={handleBrowseDrive}
            aria-label="Browse Google Drive"
          >
            <svg className="w-4 h-4" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
              <path d="m6.6 66.85 25.3-43.8 25.3 43.8z" fill="currentColor" />
              <path d="m43.85 66.85 25.3-43.8 18.15 31.45-8.35 14.35h-35.1z" fill="currentColor" fillOpacity="0.7" />
              <path d="m87.3 52.5-18.15-31.45-18.15-31.45h-36.3l18.15 31.45z" fill="currentColor" fillOpacity="0.5" />
            </svg>
            Browse Google Drive
          </Button>
        )}
      </div>

      <div
        className={cn(
          "group relative w-full border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ease-in-out cursor-pointer focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          dragActive
            ? "border-primary bg-accent"
            : "border-muted-foreground/25 hover:border-primary hover:bg-muted/30"
        )}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="file-upload"
          data-testid="file-upload-input"
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          onChange={handleChange}
          accept=".epub,.zip"
          multiple
          disabled={isImporting}
          aria-label="Upload EPUB or ZIP files"
        />

        {isImporting ? (
          <ImportProgressUI />
        ) : (
          <div className="flex flex-col items-center justify-center space-y-3 pointer-events-none">
            <div className={cn(
              "p-4 rounded-full bg-muted transition-all duration-200",
              dragActive ? "bg-background scale-110" : "group-hover:bg-background"
            )}>
              <UploadCloud className={cn("h-8 w-8 text-muted-foreground transition-colors", dragActive && "text-primary")} />
            </div>
            <div className="space-y-1">
              <p className="text-lg font-medium text-foreground">
                {dragActive ? (
                  "Release to drop files here"
                ) : (
                  <>Drop EPUBs or ZIPs here, or <span className="text-primary hover:underline">browse</span></>
                )}
              </p>
              <p className="text-sm text-muted-foreground">
                Supports .epub and .zip archives
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Per-file outcome summary, shown after a batch import completes */}
      {!isImporting && <ImportProgressUI />}

      <ReplaceBookDialog
        isOpen={!!currentDuplicate}
        onClose={() => setDuplicateQueue(prev => prev.slice(1))}
        onConfirm={async () => {
          if (!currentDuplicate) return;
          try {
            await controller.replaceFile(currentDuplicate);
            showToast("Book replaced successfully", "success");
          } catch (error) {
            showToast(`Replace failed: ${presentError(error)}`, 'error');
            throw error;
          }
        }}
        fileName={currentDuplicate?.name || ''}
      />

    </div>
  );
};
